import React, { useEffect, useState } from "react";
import { Column,  Button } from "devextreme-react/data-grid";
import { AdminTable } from "./AdminTable";
import { useDispatch, useSelector } from "react-redux";
import { createUser, deleteUser, getUsersWithRoles, updateUser } from "../../redux/users/thunks";
import { getRoles } from "../../redux/roles/thunks";
import { selectUsersLoading, selectUsersPag } from "../../redux/users/selectors";
import { selectRoles, selectRolesLoading } from "../../redux/roles/selectors";
import { openModal } from "../../redux/modal/modalSlice";
import { getServices } from "../../redux/services/thunks";
import { generate } from "generate-password-browser";
import { selectSuppliers } from "../../redux/suppliers/selectors";
import { getSuppliers } from "../../redux/suppliers/thunks";
import { UserDialogue } from "./UserDialogue";
import { useTranslation } from "react-i18next";

const initialSortOptions = {
    columna: "userId",
    sortOrderAsc: true

}

const initialRowData={
    userName: "",
    roles: [{roleId: null, services: [], externalId: generate({length: 8}) }]
}

const initialDialogData= {title: "Nuevo usuario", open: false, operation: "create"}

function UserManagement() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const users = useSelector(selectUsersPag);
    const roles = useSelector(selectRoles);
    const suppliers = useSelector(selectSuppliers);
    const usersLoading = useSelector(selectUsersLoading);
    const rolesLoading = useSelector(selectRolesLoading);
    const loading = (usersLoading === "loading" || rolesLoading === "loading")? "loading" : "succeeded";
    const numElements = [10, 20, 30];
    const [sortOptions, setSortOptions] = useState(initialSortOptions)
    const [selectedRowData, setSelectedRowData] = useState(initialRowData)
    const [dialogData, setDialogData] = useState(initialDialogData)

    useEffect(() => {
        getUsers(mapPageInfo(users));
        dispatch(getRoles());
        dispatch(getServices());
        dispatch(getSuppliers());
    }, [])

    function mapPageInfo(users) {
        return {
            pageIndex: users.pageIndex ?? 0,
            pageSize: users.pageSize ?? 10,
            orderByField:  users.columna ?? "userId",
            orderByAscending:  users.sortOrderAsc ?? true
        }
    }

    const getUsers = (pageInfo) => {
        let query = `?pageIndex=${pageInfo.pageIndex}&pageNum=${pageInfo.pageSize}`
        if(pageInfo.searchString?.trim().length > 0){
            query = query.concat(`&searchString=${pageInfo.searchString.trim()}`)
        }
        if(pageInfo.orderByField)
            {
                query = query.concat(`&orderByField=${pageInfo.orderByField}&orderByAscending=${pageInfo.orderByAscending}`)
            }
        dispatch(getUsersWithRoles({ query }));
    }

    const handleSaveNew = (userForm) => {
        if(!validateUserData(userForm)){
                return;
        }

        if (userForm.userName !== "" && userForm.password !== "" && userForm.roles.length > 0) {
            const params = {
                body: {
                    UserName: userForm.userName,
                    Password: userForm.password,
                    ForcePasswordChange: userForm.forcePasswordChange,
                    Roles: userForm.roles,
                    SupplierId: userForm.supplierId
                }
            }
            dispatch(createUser(params)).then((data) => {
                if (data.error)
                    dispatch(openModal({ type: "warning", message: data.error.message }))
                else {
                    getUsers(mapPageInfo({...users,...sortOptions}));
                    closeDialog();
                }
            })
        } else {
            closeDialog();
        }
    }

    const handleSaveEdit = (userForm) => {
            if(!validateUserData(userForm)){
                return;
            }

            const params = {
                body: {
                    UserName: userForm.userName,
                    ResetPassword: userForm.resetPassword,
                    ForcePasswordChange: userForm.forcePasswordChange,
                    Roles: userForm.roles,
                    SupplierId: userForm.supplierId
                },
                param: userForm.userId
            }
            dispatch(updateUser(params)).then((data) => {
                if (data.error)
                    dispatch(openModal({ type: "warning", message: data.error.message }))
                else {
                    getUsers(mapPageInfo({...users,...sortOptions}));
                    closeDialog();
                }
            })
        
    }

    const handleDelete = (userId) => {
        dispatch(deleteUser({ param: userId })).then((data) => {
            if (data.error)
                dispatch(openModal({ type: "warning", message: data.error.message }))
            else {
                let updatedPageIndex = users.pageIndex;

                if (users.elements.length === 1) {
                    updatedPageIndex = Math.max(0, users.pageIndex - 1);
                }

                const updatedUsers = {
                    ...users,
                    pageIndex: updatedPageIndex,
                };

                getUsers(mapPageInfo({...updatedUsers,...sortOptions}));
                closeDialog()
            }
        })
    }

    const handleCalculateRoleCellDisplayValue = (rowData) => {
        return( <div style={{display: "flex", flexDirection: "column", rowGap: "5px"}}>
                {rowData.data.roles?.map(role=>
                        <div key={role.roleId}>
                            <span>{role.role.roleDescription}</span>
                            {role.services.length > 0 && 
                                <>
                                <span>:</span> <span>{role.services.map((service, index, array)=>`${service.serviceName}${index !== array.length-1 ? ", " : ""}`)}</span>
                                </>
                            }    
                        </div>)
                }
            </div>
        )
    }

    function validateUserData(userData){
        if(userData.userName.trim() === ""){
            dispatch(openModal({type: "warning", message: t("UsernameRequired")}))
            return false
        }
        if(userData.roles.length === 1 && !userData.roles[0].roleId){
            dispatch(openModal({type: "warning", message: t("RoleRequired")}))
            return false
        }
        if(userData.roles.some(role=> !role.roleId && role.serviceIds.length > 0)){
            dispatch(openModal({type: "warning", message: t("ServicesRoleRequired")}))
            return false
        }

        // Validar seleção de Fornecedor caso a role supplier (Fornecedor) seja escolhida
        const supplierRole = roles.find(r => r.roleName === "supplier");
        if (supplierRole) {
            const hasSupplierRole = userData.roles.some(r => r.roleId === supplierRole.roleId);
            if (hasSupplierRole && !userData.supplierId) {
                dispatch(openModal({type: "warning", message: t("SupplierRequired")}))
                return false;
            }
        }

        return true;
    }

    function handleNewDialogOpen() {
        setSelectedRowData(initialRowData);
        setDialogData({title: t("NewUser"), open: true, operation: "create"})
    }

    
    function handleEditDialogOpen(rowdata){
        setSelectedRowData(rowdata.row.data)
        setDialogData({title: t("EditUser"), open: true, operation: "edit"})
    }

    function handleDeleteDialogOpen(rowData){
        setSelectedRowData(rowData.row.data)
        setDialogData({title: t("DeleteUser"), open: true, operation: "delete"})
    }

    function closeDialog(){
        setSelectedRowData(initialRowData)
        setDialogData(initialDialogData)
    }

    const handleSupplierCellRender = (rowData) => {
        const supplier = suppliers.find(s => s.supplierId === rowData.data.supplierId);
        return supplier ? <span>{supplier.description}</span> : <span>-</span>;
    }

    const columnsTaxonomy = [
        <Column allowEditing={false} key={"users-1"} dataField = "userId" caption="Id" alignment="left" />,
        <Column key={"users-2"} dataField = "userName" caption={t("Username")} />,
        <Column key={"users-3"} lookup={{multiple: true, allowClearing: false, dataSource: roles, displayExpr:'roleDescription', valueExpr:'roleId'}} dataField = "roles" cellRender={handleCalculateRoleCellDisplayValue} caption={t("Roles")} />,
        <Column key={"users-4"} dataField = "supplierId" cellRender={handleSupplierCellRender} caption={t("Supplier")} alignment="left" />
    ]

    const onSubmitOperation = (e, data) => {
        const operation = dialogData.operation

        switch (operation) {
            case "create": 
                handleSaveNew(data);
                break;
            case "edit": 
                handleSaveEdit(data);
                break;
            case "delete": 
                handleDelete(data.userId);
                break;
            default: break;
        }

    } 

    return (
        <div style={{width: "100%"}}>
            <AdminTable 
                defaultSortField={"userId"}
                sectionTitle={t("Users")}
                masterTitle={t("User")}
                itemIdField={"userId"} 
                itemDescriptionField={"userName"} 
                loading={loading} 
                customCreation
                customEdition
                onCustomEditingStart={handleEditDialogOpen}
                onCustomDeletingStart={handleDeleteDialogOpen}
                dataSource={users} 
                columnasTabla={columnsTaxonomy} 
                numElements={numElements} 
                getData = { getUsers } 
                onCreate={handleNewDialogOpen} 
                onDelete={handleDelete}
                sortField={sortOptions}
                setsortField={setSortOptions}
            />
            <UserDialogue open={dialogData.open} data={selectedRowData} handleDialogClose={closeDialog} onSubmitOperation={onSubmitOperation} title={dialogData.title} operation={dialogData.operation} />
        </div>
    )
}

export default UserManagement
